import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Handshake, Copy, ArrowRight, Check, Mail, MapPin, Zap, Shield, Users, Star, Megaphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnershipPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', type: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const type = searchParams.get('type');
    if (type && ['add_address', 'clone', 'affiliate'].includes(type)) {
      setForm(f => ({ ...f, type }));
    }
    if (window.location.hash === '#contact') {
      setTimeout(() => {
        const el = document.getElementById('contact');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t('partnership.fillRequired'));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact/partnership`, form);
      setSent(true);
      toast.success(t('partnership.msgSentSuccess'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('partnership.sendError'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] font-body" data-testid="partnership-page">
      <SEO titleKey="seo.partnership.title" descriptionKey="seo.partnership.description" />

      {/* Hero */}
      <section className="bg-[#1A3C34] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #C77D63 0%, transparent 50%), radial-gradient(circle at 80% 50%, #C77D63 0%, transparent 50%)' }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C77D63]/20 border border-[#C77D63]/30 rounded-full text-sm font-semibold text-[#C77D63] mb-8">
            <Handshake className="w-4 h-4" />
            {t('partnership.indexBadge')}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight">
            {t('partnership.indexTitle')}
            <br />
            <span className="text-[#C77D63]">{t('partnership.indexTitleAccent')}</span>
          </h1>
          <p className="mt-6 text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            {t('partnership.indexIntro')}
          </p>
        </div>
      </section>

      {/* Offers */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A3C34] text-center tracking-tight mb-4">
            {t('partnership.ourFormulas')}
          </h2>
          <p className="text-center text-[#737373] max-w-lg mx-auto mb-14">
            {t('partnership.ourFormulasDesc')}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Option 1 */}
            <button
              type="button"
              onClick={() => navigate('/partenariat/ajouter-mon-adresse')}
              className="text-left bg-white border border-[#E2E2DF] rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 hover:border-[#C77D63]/40 transition-all duration-300 group"
              data-testid="offer-add-address"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#C77D63]/10 flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-[#C77D63]" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#1A3C34]">{t('partnership.card1Title')}</h3>
              <p className="mt-3 text-sm text-[#737373] leading-relaxed">
                {t('partnership.card1Desc')}
              </p>
              <ul className="mt-5 space-y-2.5">
                {[t('partnership.card1F1'), t('partnership.card1F2'), t('partnership.card1F3'), t('partnership.card1F4')].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#1A3C34]">
                    <Check className="w-4 h-4 text-[#C77D63] mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#C77D63] group-hover:gap-2.5 transition-all">
                {t('partnership.learnMore')} <ArrowRight className="w-4 h-4" />
              </span>
            </button>

            {/* Option 2 - Populaire */}
            <button
              type="button"
              onClick={() => navigate('/partenariat/cloner-le-site')}
              className="text-left bg-white border-2 border-[#C77D63] rounded-xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative group"
              data-testid="offer-clone"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-[#C77D63] text-white text-xs font-bold px-4 py-1.5 rounded-full">{t('partnership.popular')}</span>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-[#C77D63]/10 flex items-center justify-center mb-6">
                <Copy className="w-7 h-7 text-[#C77D63]" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#1A3C34]">{t('partnership.card2Title')}</h3>
              <p className="mt-3 text-sm text-[#737373] leading-relaxed">
                {t('partnership.card2Desc')}
              </p>
              <ul className="mt-5 space-y-2.5">
                {[t('partnership.card2F1'), t('partnership.card2F2'), t('partnership.card2F3'), t('partnership.card2F4'), t('partnership.card2F5'), t('partnership.card2F6')].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#1A3C34]">
                    <Check className="w-4 h-4 text-[#C77D63] mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#C77D63] group-hover:gap-2.5 transition-all">
                {t('partnership.learnMore')} <ArrowRight className="w-4 h-4" />
              </span>
            </button>

            {/* Option 3 */}
            <button
              type="button"
              onClick={() => navigate('/partenariat/affiliation')}
              className="text-left bg-white border border-[#E2E2DF] rounded-xl p-8 hover:shadow-lg hover:-translate-y-1 hover:border-[#C77D63]/40 transition-all duration-300 group"
              data-testid="offer-affiliate"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#C77D63]/10 flex items-center justify-center mb-6">
                <Megaphone className="w-7 h-7 text-[#C77D63]" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#1A3C34]">{t('partnership.card3Title')}</h3>
              <p className="mt-3 text-sm text-[#737373] leading-relaxed">
                {t('partnership.card3Desc')}
              </p>
              <ul className="mt-5 space-y-2.5">
                {[t('partnership.card3F1'), t('partnership.card3F2'), t('partnership.card3F3'), t('partnership.card3F4'), t('partnership.card3F5')].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#1A3C34]">
                    <Check className="w-4 h-4 text-[#C77D63] mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#C77D63] group-hover:gap-2.5 transition-all">
                {t('partnership.learnMore')} <ArrowRight className="w-4 h-4" />
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Why join */}
      <section className="bg-[#1A3C34] py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white text-center tracking-tight mb-14">
            {t('partnership.whyJoin')}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Zap, title: t('partnership.why1Title'), desc: t('partnership.why1Desc') },
              { icon: Shield, title: t('partnership.why2Title'), desc: t('partnership.why2Desc') },
              { icon: Users, title: t('partnership.why3Title'), desc: t('partnership.why3Desc') },
              { icon: Star, title: t('partnership.why4Title'), desc: t('partnership.why4Desc') },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#C77D63]/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-[#C77D63]" />
                </div>
                <h3 className="font-heading text-base font-bold text-white">{item.title}</h3>
                <p className="mt-2 text-sm text-white/50 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="py-20" id="contact">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A3C34] tracking-tight">
              {t('partnership.contactUs')}
            </h2>
            <p className="mt-3 text-base text-[#737373]">
              {t('partnership.contactDesc')}
            </p>
          </div>

          {sent ? (
            <div className="bg-white border border-[#E2E2DF] rounded-xl p-12 text-center" data-testid="partnership-sent">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#1A3C34]">{t('partnership.msgSent')}</h3>
              <p className="mt-3 text-sm text-[#737373]">{t('partnership.msgSentDesc')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white border border-[#E2E2DF] rounded-xl p-8 space-y-5 shadow-sm" data-testid="partnership-form">
              <div>
                <label className="text-sm font-semibold text-[#1A3C34]">{t('partnership.interestedIn')}</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                  {[
                    { value: 'add_address', label: t('partnership.optAddAddress') },
                    { value: 'clone', label: t('partnership.optClone') },
                    { value: 'affiliate', label: t('partnership.optAffiliate') },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, type: opt.value }))}
                      className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                        form.type === opt.value
                          ? 'border-[#C77D63] bg-[#C77D63]/10 text-[#C77D63]'
                          : 'border-[#E2E2DF] text-[#737373] hover:border-[#C77D63]/50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-[#1A3C34]">{t('partnership.fieldName')}</label>
                  <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="mt-1 h-11 border-[#E2E2DF] rounded-lg" placeholder={t('partnership.fieldNamePh')} required />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#1A3C34]">{t('partnership.fieldEmail')}</label>
                  <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="mt-1 h-11 border-[#E2E2DF] rounded-lg" placeholder={t('partnership.fieldEmailPh')} required />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1A3C34]">{t('partnership.fieldPhone')}</label>
                <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="mt-1 h-11 border-[#E2E2DF] rounded-lg" placeholder="+32 ..." />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1A3C34]">{t('partnership.fieldMessage')}</label>
                <textarea
                  value={form.message}
                  onChange={e => setForm(f => ({...f, message: e.target.value}))}
                  className="mt-1 w-full p-3 text-sm border border-[#E2E2DF] rounded-lg resize-none h-28 focus:outline-none focus:border-[#C77D63]"
                  placeholder={t('partnership.fieldMessagePh')}
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full h-12 bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg font-bold text-base shadow-md shadow-[#C77D63]/20 transition-all"
                data-testid="partnership-submit"
              >
                {sending ? t('partnership.sendingMsg') : t('partnership.sendRequest')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          )}

          {/* Direct contact */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-[#737373]">
            <a href="mailto:info@flexi-location.be" className="flex items-center gap-2 hover:text-[#C77D63] transition-colors">
              <Mail className="w-4 h-4" /> info@flexi-location.be
            </a>
            <span className="hidden sm:block text-[#E2E2DF]">|</span>
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" /> 304 Chaussée de Stockel, 1200 Woluwe-Saint-Lambert
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
